/*
 * Portions of this component's visual styling are adapted from shadcn/ui.
 * Copyright (c) 2023 shadcn. Licensed under the MIT License.
 */

import { IconX } from "./icons/x.js";

function requestClose(host: HTMLElement): void {
  host.dispatchEvent(new CustomEvent("close", { bubbles: true, composed: true }));
}

const styles = css`
  :host {
    display: contents;
  }

  .overlay {
    background: rgb(0 0 0 / 50%);
    inset: 0;
    position: fixed;
    z-index: 50;
  }

  .panel {
    background: var(--background, #ffffff);
    box-shadow: 0 10px 38px rgb(0 0 0 / 18%);
    box-sizing: border-box;
    color: var(--foreground, #09090b);
    display: flex;
    flex-direction: column;
    gap: 16px;
    overflow: auto;
    padding: 24px;
    position: fixed;
  }

  :host(:not([side])) .panel,
  :host([side="right"]) .panel {
    border-left: 1px solid var(--border, #e4e4e7);
    bottom: 0;
    right: 0;
    top: 0;
    width: min(380px, 100%);
  }

  :host([side="left"]) .panel {
    border-right: 1px solid var(--border, #e4e4e7);
    bottom: 0;
    left: 0;
    top: 0;
    width: min(380px, 100%);
  }

  :host([side="top"]) .panel {
    border-bottom: 1px solid var(--border, #e4e4e7);
    left: 0;
    right: 0;
    top: 0;
  }

  :host([side="bottom"]) .panel {
    border-top: 1px solid var(--border, #e4e4e7);
    bottom: 0;
    left: 0;
    right: 0;
  }

  .title {
    font-size: 1.05rem;
    font-weight: 600;
    padding-right: 28px;
  }

  .title:empty {
    display: none;
  }

  .close {
    align-items: center;
    background: transparent;
    border: 0;
    border-radius: 6px;
    color: var(--muted-foreground, #71717a);
    cursor: pointer;
    display: inline-flex;
    height: 24px;
    justify-content: center;
    padding: 0;
    position: absolute;
    right: 16px;
    top: 16px;
    width: 24px;
  }

  .close:hover {
    background: var(--muted, #f4f4f5);
    color: var(--foreground, #18181b);
  }

  .icon {
    height: 16px;
    width: 16px;
  }
`;

export class AppSheet extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppSheet)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["open","data-open","title","data-title"];

  #open = false;
  #title = "";
  #openSet = false;
  #titleSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #attr0;
  #attrLast0;
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get open() {
    return this.#open;
  }

  set open(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#open, next)) return;
    this.#open = next;
    this.#openSet = true;
    this.#updateBindings('open');
  }

  get title() {
    return this.#title;
  }

  set title(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#title, next)) return;
    this.#title = next;
    this.#titleSet = true;
    this.#updateBindings('title');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-open')) this.open = this.dataset.open;
    if (this.hasAttribute('data-title')) this.title = this.dataset.title;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.open = props.open;
      this.title = props.title;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (this.#isCSR || this.#bindingsMounted) return;
    this.#syncHydratedProps();
    if (!this.#openSet) this.open = this.dataset.open;
    if (!this.#titleSet) this.title = this.dataset.title;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'open':
      case 'data-open':
        this.open = newValue;
        break;
      case 'title':
      case 'data-title':
        this.title = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    
    return `<style>${this.#escText(styles)}</style><div class="overlay" style="${this.#escAttr(this.#open ? "display:block" : "display:none")}" data-argon="2315" data-argon-bind="0"><div class="panel" role="dialog" aria-modal="true"><button class="close" type="button" aria-label="Close" data-argon="2560"><span class="icon"><icon-x><!--argon-text:0-->${""}<!--/argon-text:0--></icon-x></span></button><div class="title"><!--argon-text:1-->${this.#escText(this.#title)}<!--/argon-text:1--></div><div class="content"><slot></slot></div></div></div>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'title') {
      const __argonNext = `${this.#escText(this.#title)}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if ((prop === undefined || prop === 'open') && this.#attr0) {
      const __argonNext = `${this.#open ? "display:block" : "display:none"}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('style', __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '2315').forEach(el => this.#listen(el, '2315', 'click', (event: Event) => {
    if (event.target === event.currentTarget)
        requestClose(this);
}));
    this.#eventTargets(scope, '2560').forEach(el => this.#listen(el, '2560', 'click', () => requestClose(this)));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
